import React, { useCallback, useState } from "react";

import { PopupContext } from "./PopupContext";

import Popup from "@/components/ui/v1/popup/Popup";

interface PopupState {
  content: React.ReactNode | null;
}

const defaultState: PopupState = {
  content: null,
};

export const PopupProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [{ content }, setPopupState] = useState<PopupState>(defaultState);
  const [canClose, setCanClose] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const openPopup = useCallback((content: React.ReactNode) => {
    setPopupState({ content });
    setIsOpen(true);
  }, []);

  const closePopup = useCallback(() => {
    if (canClose) {
      setIsOpen(false);
      // Clear content after animation completes
      setTimeout(() => {
        setPopupState(defaultState);
      }, 200);
    }
  }, [canClose]);

  const resetPopup = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setPopupState(defaultState);
      setCanClose(true);
    }, 200);
  }, []);

  const disableClose = useCallback(() => {
    setCanClose(false);
  }, []);

  const enableClose = useCallback(() => {
    setCanClose(true);
  }, []);

  const contextValue = {
    canClose,
    closePopup,
    disableClose,
    enableClose,
    resetPopup,
    isOpen,
    openPopup,
  };

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
      {isOpen && <Popup onClose={closePopup}>{content}</Popup>}
    </PopupContext.Provider>
  );
};
